import { useNavigate } from 'react-router-dom'
import { Chip } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { useAuth } from '@/contexts/AuthContext'

interface UserNameChipProps {
  userId: string
  displayName: string
  bgColor?: string
  textColor?: string
  sx?: SxProps<Theme>
}

/**
 * A clickable chip that displays a user's name and navigates to their profile.
 * Navigates to /profile for the current user, or /users/:userId for others.
 * Stops event propagation so it works safely inside CardActionArea.
 */
export default function UserNameChip({ userId, displayName, bgColor, textColor, sx }: UserNameChipProps) {
  const navigate = useNavigate()
  const { user } = useAuth()

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (user?.userId === userId) {
      navigate('/profile')
    } else {
      navigate(`/users/${userId}`)
    }
  }

  if (!bgColor) {
    return (
      <Chip
        label={displayName}
        size="small"
        variant="outlined"
        onClick={handleClick}
        sx={sx}
      />
    )
  }

  return (
    <Chip
      label={displayName}
      size="small"
      onClick={handleClick}
      sx={{
        bgcolor: bgColor,
        color: textColor ?? '#ffffff',
        fontWeight: 600,
        border: '1px solid',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        ...sx,
      }}
    />
  )
}
